/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-03 14:54:54
 **/

import { createPortal } from "react-dom";
import { useAtomValue } from "jotai";

import { Dragger, SwitchCase } from "@/frameworks/libraries/components";
import { ZIndex } from "@/frameworks/core/commons";
import { PhoneCallAtoms } from "@/frameworks/core/hooks";
import { JotaiStore } from "@/frameworks/libraries/commons";
import PhoneCall from "./PhoneCall/PhoneCall";
import IncomingCall from "./IncomingCall";
import ScreenShare from "./ScreenShare";

function PhoneCallPopupPortal({ uiType, ...rest }: PushServices.Call.PhoneCallPortalItem & { id: string }) {
	return createPortal(
		<>
			<Dragger
				className="draggableCall"
				key={`draggableCall${uiType}${rest.callID}`}
				zIndex={uiType === "fullSizeShare" ? ZIndex.CHAT_POPUP - 1 : ZIndex.PHONE_CALL_FLOATING}
				startCoordinate={
					uiType === "floating"
						? { x: document.documentElement.clientWidth / 2 - 208, y: 72 }
						: uiType === "receiveCall"
							? { x: document.documentElement.clientWidth - 428, y: 69 }
							: { x: 0, y: 60 }
				}
			>
				<SwitchCase
					condition={uiType}
					cases={{
						floating: <PhoneCall {...rest} key={rest.callID + "floating"} />,
						fullSizeShare: <ScreenShare {...rest} key={rest.callID + "screenShare"} />,
						receiveCall: <IncomingCall {...rest} key={rest.callID + "received"} />,
						none: null
					}}
				/>
			</Dragger>
		</>,
		document.body
	);
}

export default function PhoneCallPopupPortals() {
	const portalItems = useAtomValue(PhoneCallAtoms.Popup, { store: JotaiStore });

	return (
		<>
			{Object.entries(portalItems).map(([id, options], index) => (
				<PhoneCallPopupPortal key={index} {...options} id={id} />
			))}
		</>
	);
}
